import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Anchor, Grid } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { Controller, useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { SettingsFormContent, SettingsFormCTAs, SettingsHeader } from "@/components/pages/Settings/index";
import Text from "@/components/atoms/Text";
import CTA from "@/components/atoms/CTA";
import CheckBox from "@/components/atoms/Checkbox";
import { useAuth } from "@/contexts/AuthContext";
import { useGetCompanyTeam } from "@/services/Company";
import { useGetReceivingInspectionSettings, useUpdateReceivingInspectionSettings } from "@/services/Inspections";
import UserRoleTypeEnum from "@/constants/enums/UserRoleTypeEnum";
import { ErrorNotificationObj, SuccessNotificationObj } from "@/utils/NotificationUtil";
import { getApiError } from "@/utils/apiErrors";
import {
  ReceivingInspectionFormInput,
  ReceivingInspectionValidationSchema,
} from "./validation/receivingInspectionValidationSchema";

const DEFAULT_VALUES: ReceivingInspectionFormInput = {
  isReceivingInspectionEnabled: false,
  isDeviationConfirmationEnabled: false,
};

/** Settings → Receiving inspection: receiving inspection and deviation confirmation. */
export default function ReceivingInspectionSettings() {
  const { t } = useTranslation();
  const { currentCompany, isAdminUser } = useAuth();
  const companyId = currentCompany?.companyId as number;
  const isAdmin = isAdminUser(currentCompany);

  const { data: settings } = useGetReceivingInspectionSettings(companyId);
  const { data: team } = useGetCompanyTeam(companyId);
  const managers = (team?.activeUsers ?? []).filter((member) =>
    member.roles.includes(UserRoleTypeEnum.DEVIATION_MANAGER)
  );

  const { control, handleSubmit, setValue, setError } = useForm<ReceivingInspectionFormInput>({
    resolver: zodResolver(ReceivingInspectionValidationSchema),
    values: settings
      ? {
          isReceivingInspectionEnabled: settings.isReceivingInspectionEnabled,
          isDeviationConfirmationEnabled: settings.isDeviationConfirmationEnabled,
        }
      : DEFAULT_VALUES,
  });
  const inspectionEnabled = useWatch({ control, name: "isReceivingInspectionEnabled" });

  const { mutate: saveSettings, isPending } = useUpdateReceivingInspectionSettings(companyId, {
    onSuccess: () =>
      notifications.show(
        SuccessNotificationObj(
          t("notifications:generic.updateSuccess"),
          t("notifications:settings.receivingInspectionUpdated"),
          "receiving-inspection-updated-success"
        )
      ),
    onError: (error) => {
      const { errorCode, reason } = getApiError(error);
      notifications.show(
        ErrorNotificationObj(
          t("notifications:generic.updateError"),
          // 242 breaks a settings rule. The form checks each rule first, so only a case it misses shows the
          // backend's own (English) reason.
          errorCode === 242 && reason ? reason : t("notifications:settings.receivingInspectionUpdateError"),
          "receiving-inspection-updated-error"
        )
      );
    },
  });

  const onSubmit = (data: ReceivingInspectionFormInput) => {
    // The backend refuses confirmation (242) until someone holds the role that confirms.
    if (data.isDeviationConfirmationEnabled && team && managers.length === 0) {
      setError("isDeviationConfirmationEnabled", { message: "validation:deviationManagerRequired" });
      return;
    }
    saveSettings(data);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} data-testid="receiving-inspection-settings">
      <SettingsFormContent>
        <Grid>
          <SettingsHeader>
            <Text size="md" strong={true} display="technical" tt="uppercase">
              {t("settings:receivingInspection.title")}
            </Text>
          </SettingsHeader>
          <Grid.Col span={12}>
            <Text size="sm" color="secondary">
              {t("settings:receivingInspection.description")}
            </Text>
          </Grid.Col>

          <Grid.Col span={12}>
            <Controller
              control={control}
              name="isReceivingInspectionEnabled"
              render={({ field }) => (
                <CheckBox
                  id="receiving-inspection-enabled"
                  label={t("settings:receivingInspection.enabled")}
                  description={t("settings:receivingInspection.enabledDescription")}
                  checked={field.value}
                  onChange={(event) => {
                    const checked = event.currentTarget.checked;
                    field.onChange(checked);
                    // Confirmation needs inspection, so both go off together.
                    if (!checked) setValue("isDeviationConfirmationEnabled", false);
                  }}
                  disabled={!isAdmin}
                />
              )}
            />
          </Grid.Col>

          <Grid.Col span={12}>
            <Controller
              control={control}
              name="isDeviationConfirmationEnabled"
              render={({ field, fieldState }) => (
                <CheckBox
                  id="deviation-confirmation-enabled"
                  label={t("settings:receivingInspection.deviationConfirmation")}
                  description={t("settings:receivingInspection.deviationConfirmationDescription")}
                  checked={field.value}
                  onChange={(event) => field.onChange(event.currentTarget.checked)}
                  error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                  disabled={!isAdmin || !inspectionEnabled}
                />
              )}
            />
          </Grid.Col>

          <Grid.Col span={12} data-testid="deviation-managers">
            <Text size="sm" color="secondary">
              {t("settings:receivingInspection.deviationManagers")}
            </Text>
            <Text size="sm">
              {managers.length
                ? managers.map((member) => `${member.firstName} ${member.lastName}`).join(", ")
                : t("settings:receivingInspection.noDeviationManagers")}
            </Text>
            {isAdmin && (
              <Anchor component={Link} to="/settings/users" size="sm" underline="always" c="inherit">
                {t("settings:receivingInspection.manageDeviationManagers")}
              </Anchor>
            )}
          </Grid.Col>
        </Grid>
      </SettingsFormContent>
      <SettingsFormCTAs>
        <CTA disabled={!isAdmin} size="lg" type="submit" loading={isPending} data-testid="submit-btn">
          {t("actions:save")}
        </CTA>
      </SettingsFormCTAs>
    </form>
  );
}
